// src/vite-env.d.ts has type definitions for all custom env variables. It
// should be edited as well when editing this file.

import { z } from "zod";

/**
 * Environment variable schema validation
 * This ensures we have proper type safety and validation for our environment variables
 */
const envSchema = z.object({
  // Environment: Vite Built-ins
  // See https://vite.dev/guide/env-and-mode for more info
  DEV: z.boolean({
    required_error:
      "DEV is required, but it should have been autogenerated by Vite as a built-in constant. Something went wrong.",
  }),
  PROD: z.boolean({
    required_error:
      "PROD is required, but it should have been autogenerated by Vite as a built-in constant. Something went wrong.",
  }),

  // Environment: Our custom ones
  VITE_NODE_ENV: z.enum(["development", "staging", "production", "test"], {
    required_error: "VITE_NODE_ENV is required",
    invalid_type_error:
      "VITE_NODE_ENV must be one of: development, staging, production, test",
  }),
  VITE_IS_FEDRAMP: z
    .string()
    .optional()
    .transform((val) => val !== "false"),

  // API URLs
  VITE_APP_URL: z
    .string({
      required_error: "VITE_APP_URL is required",
      invalid_type_error: "VITE_APP_URL must be a string",
    })
    .url({
      message:
        "VITE_APP_URL must be a valid URL (e.g., https://app.example.com)",
    }),
  VITE_API_BASE_URL: z
    .string({
      required_error: "VITE_API_BASE_URL is required",
      invalid_type_error: "VITE_API_BASE_URL must be a string",
    })
    .url({
      message:
        "VITE_API_BASE_URL must be a valid URL (e.g., https://api.example.com)",
    }),
  VITE_IDENTITY_SERVICE_URL: z
    .string({
      required_error: "VITE_IDENTITY_SERVICE_URL is required",
      invalid_type_error: "VITE_IDENTITY_SERVICE_URL must be a string",
    })
    .url({
      message:
        "VITE_IDENTITY_SERVICE_URL must be a valid URL (e.g., https://identity.example.com)",
    }),
  VITE_CSRF_TOKEN_OVERRIDE: z
    .string({
      invalid_type_error: "VITE_CSRF_TOKEN_OVERRIDE must be a string",
    })
    .optional(),

  // Statsig
  VITE_STATSIG_ENABLED: z
    .string()
    .optional()
    .transform((val) => val !== "false"),
  VITE_INVISIBLE_STATSIG_PROXY: z
    .string()
    .optional()
    .transform((val) => (val === "" ? undefined : val))
    .pipe(z.string().url().optional()),
  VITE_STATSIG_ENVIRONMENT_TIER: z
    .enum(["development", "production", "staging"])
    .optional(),
  VITE_STATSIG_CLIENT_SDK_KEY: z.string({
    invalid_type_error: "STATSIG_CLIENT_SDK_KEY must be a string",
  }),

  // DataDog RUM
  VITE_DATADOG_ENABLED: z
    .string()
    .optional()
    .transform((val) => val !== "false"),
  VITE_DATADOG_APPLICATION_ID: z
    .string({
      invalid_type_error: "VITE_DATADOG_APPLICATION_ID must be a string",
    })
    .optional(),
  VITE_DATADOG_CLIENT_TOKEN: z
    .string({
      invalid_type_error: "VITE_DATADOG_CLIENT_TOKEN must be a string",
    })
    .optional(),
  VITE_DATADOG_SERVICE_NAME: z
    .string({
      invalid_type_error: "VITE_DATADOG_SERVICE_NAME must be a string",
    })
    .optional(),
  VITE_DATADOG_ENV: z
    .string({
      invalid_type_error: "VITE_DATADOG_ENV must be a string",
    })
    .optional(),
  VITE_DATADOG_SITE: z
    .string({
      invalid_type_error: "VITE_DATADOG_SITE must be a string",
    })
    .optional(),
  VITE_DATADOG_SAMPLING_RATE: z
    .string({
      invalid_type_error: "VITE_DATADOG_SAMPLING_RATE must be a string",
    })
    .optional()
    .transform((val) => {
      if (val === undefined) return undefined;
      const num = Number(val);
      if (isNaN(num)) {
        throw new Error(
          "VITE_DATADOG_SAMPLING_RATE must be a valid number, got: " + val,
        );
      }
      return num;
    }),
  VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE: z
    .string({
      invalid_type_error:
        "VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE must be a string",
    })
    .optional()
    .transform((val) => {
      if (val === undefined) return undefined;
      const num = Number(val);
      if (isNaN(num)) {
        throw new Error(
          "VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE must be a valid number",
        );
      }
      return num;
    }),

  // Embed: Comma-separated allowlist of origins for postMessage (e.g. "https://app.example.com,https://localhost:3000")
  VITE_EMBED_ALLOWED_ORIGINS: z
    .string()
    .optional()
    .transform((val) =>
      val && val.trim() ? val.split(",").map((o) => o.trim()) : undefined,
    ),

  // Mixpanel
  VITE_MIXPANEL_TOKEN: z
    .string({
      invalid_type_error: "VITE_MIXPANEL_TOKEN must be a string",
    })
    .optional(),
  VITE_MIXPANEL_ENABLED: z
    .string()
    .optional()
    .transform((val) => val === "true"),
});

// Process but don't throw on missing values for better error messages
const processEnv = {
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,

  VITE_NODE_ENV: import.meta.env.VITE_NODE_ENV,
  VITE_IS_FEDRAMP: import.meta.env.VITE_IS_FEDRAMP || "false",

  VITE_APP_URL: import.meta.env.VITE_APP_URL,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  VITE_IDENTITY_SERVICE_URL: import.meta.env.VITE_IDENTITY_SERVICE_URL,
  VITE_CSRF_TOKEN_OVERRIDE: import.meta.env.VITE_CSRF_TOKEN_OVERRIDE,

  VITE_STATSIG_ENABLED: import.meta.env.VITE_STATSIG_ENABLED,
  VITE_INVISIBLE_STATSIG_PROXY: import.meta.env.VITE_INVISIBLE_STATSIG_PROXY,
  VITE_STATSIG_ENVIRONMENT_TIER:
    import.meta.env.VITE_STATSIG_ENVIRONMENT_TIER || "development",
  VITE_STATSIG_CLIENT_SDK_KEY:
    import.meta.env.VITE_STATSIG_CLIENT_SDK_KEY || "client-placeholderKey",

  VITE_DATADOG_ENABLED: import.meta.env.VITE_DATADOG_ENABLED,
  VITE_DATADOG_APPLICATION_ID: import.meta.env.VITE_DATADOG_APPLICATION_ID,
  VITE_DATADOG_CLIENT_TOKEN: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
  VITE_DATADOG_SERVICE_NAME:
    import.meta.env.VITE_DATADOG_SERVICE_NAME || "atomic-fe",
  VITE_DATADOG_ENV: import.meta.env.VITE_DATADOG_ENV || "development",
  VITE_DATADOG_SITE: import.meta.env.VITE_DATADOG_SITE || "datadoghq.com",
  VITE_DATADOG_SAMPLING_RATE:
    import.meta.env.VITE_DATADOG_SAMPLING_RATE || "100",
  VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE:
    import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE || "100",

  VITE_EMBED_ALLOWED_ORIGINS: import.meta.env.VITE_EMBED_ALLOWED_ORIGINS,

  VITE_MIXPANEL_TOKEN: import.meta.env.VITE_MIXPANEL_TOKEN,
  VITE_MIXPANEL_ENABLED: import.meta.env.VITE_MIXPANEL_ENABLED,
};

/**
 * Formats the error message to be more readable in the console
 */
function formatZodError(error: z.ZodError) {
  const issues = error.errors
    .map((err) => {
      const path = err.path.join(".");
      return `\n  - ${path}: ${err.message}`;
    })
    .join("");

  return `Environment validation failed:${issues}`;
}

/**
 * Provides suggestions for fixing common environment issues
 */
function getSuggestions(error: z.ZodError) {
  const suggestions = [];

  // Check for missing variables
  const missingVars = error.errors
    .filter(
      (err) => err.code === "invalid_type" && err.received === "undefined",
    )
    .map((err) => err.path.join("."));

  if (missingVars.length > 0) {
    suggestions.push(
      `Missing environment variables: ${missingVars.join(", ")}`,
      `Make sure to define these in your .env file or environment.`,
      `Check that your .env file exists and is properly formatted.`,
    );
  }

  // Check for URL format issues
  const urlErrors = error.errors.filter(
    (err) => err.code === "invalid_string" && err.validation === "url",
  );

  if (urlErrors.length > 0) {
    suggestions.push(
      `URL format issues detected. URLs must start with http:// or https://`,
    );
  }

  return suggestions.length > 0
    ? `\n\nSuggestions to fix these issues:\n- ${suggestions.join("\n- ")}`
    : "";
}

// Function to validate environment variables
export function validateEnv() {
  const result = envSchema.safeParse(processEnv);

  if (!result.success) {
    const formattedError = formatZodError(result.error);
    const suggestions = getSuggestions(result.error);

    // Generate a more helpful error message
    const errorMessage = `${formattedError}${suggestions}\n\nSee .env.example for reference.`;

    console.error("\x1b[31m%s\x1b[0m", errorMessage);
    throw new Error("Environment validation failed. See console for details.");
  }

  return result.data;
}

// Validate and export environment variables
export const env = validateEnv();

// Export the type
export type Env = z.infer<typeof envSchema>;
